import { useId } from 'react'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Heading3 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { IconButton, SplitButton } from '@ui/components/Button'
import type { SplitButtonAction } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { Tooltip } from '@ui/components/Tooltip'
import { TourTarget } from '@ui/components/Tourtip/TourTarget'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { ArrowCircleLeftRightIcon } from '@ui/icons/ArrowCircleLeftRight.icon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'
import type { IconRendererType } from '@ui/icons/utils/IconRenderer'

import { useWindowResize } from '@utils/domUtils'

import { Box } from '@shared/components/Box'
import { CopyLink } from '@shared/components/CopyLink'
import { Loading } from '@shared/components/Loading/Loading'

import type { CurrentWalletView, WalletActionKind, WalletActionView } from '../wallets.types'
import { WalletCurrencyIcon } from './WalletCurrencyIcon'

import styles from './CurrentWalletSection.module.scss'

interface CurrentWalletSectionProps {
  wallet: CurrentWalletView
}

const actionIcons: Record<WalletActionKind, IconRendererType> = {
  deposit: ArrowCircleIcon.Down,
  withdraw: ArrowCircleIcon.UpRight,
  transfer: ArrowCircleLeftRightIcon,
}

const actionTourTargets: Record<WalletActionKind, string> = {
  deposit: 'wallets.deposit',
  withdraw: 'wallets.withdrawal',
  transfer: 'wallets.transfer',
}

const getMobileActions = (
  items: WalletActionView[],
  mobileOrder?: WalletActionKind[]
): WalletActionView[] => {
  if (!mobileOrder?.length) return items

  const orderedActions = mobileOrder.reduce<WalletActionView[]>((actions, kind) => {
    const action = items.find((item) => item.kind === kind)
    if (action && !actions.includes(action)) actions.push(action)
    return actions
  }, [])

  return [...orderedActions, ...items.filter((item) => !orderedActions.includes(item))]
}

const getContentLabel = (content: React.ReactNode, fallback: string) =>
  typeof content === 'string' ? content : fallback

export const CurrentWalletSection: React.FC<CurrentWalletSectionProps> = ({ wallet }) => {
  const headingId = useId()
  const isMobile = useWindowResize()
  const { actions } = wallet
  const primaryAction = actions.items.find((action) => action.kind === actions.primaryAction)
  const secondaryActions = actions.items.filter((action) => action.kind !== actions.primaryAction)
  const mobileActions = getMobileActions(actions.items, actions.mobileOrder)
  const allActionsDisabled =
    actions.items.length > 0 && actions.items.every((action) => action.disabled)

  const toSplitButtonAction = (
    action: WalletActionView,
    includeIcon = true
  ): SplitButtonAction => ({
    label: action.label,
    icon: includeIcon ? actionIcons[action.kind] : undefined,
    disabled: !!action.disabled || !!action.loading,
    disabledTooltip:
      action.disabled && !action.loading ? actions.actionUnavailableMessage : undefined,
    onClick: action.onClick,
  })

  return (
    <TourTarget target='wallets.wallet'>
      <section className={styles.section} aria-labelledby={headingId}>
        <Box flex align='center' gap='x4'>
          <Body2 as='h2' id={headingId} medium color='text-med-em'>
            {wallet.balanceLabel}
          </Body2>
          <Tooltip content={wallet.information} direction='bottom'>
            <IconButton.Ghost
              icon={InfoIcon}
              size='xs'
              aria-label={getContentLabel(wallet.information, wallet.balanceLabel)}
            />
          </Tooltip>
        </Box>

        <Box flex align='center' justify='space-between' gap='x24' mt='x12'>
          <Heading3 className={styles.balance} color='text-high-em' medium sensitive>
            {wallet.balance}
          </Heading3>
          {primaryAction && (
            <TourTarget target='wallets.actions'>
              <Box
                className={styles.desktopActions}
                aria-busy={actions.items.some((action) => action.loading) || undefined}
              >
                <SplitButton
                  primaryAction={toSplitButtonAction(primaryAction, false)}
                  actions={secondaryActions.map((action) => toSplitButtonAction(action))}
                  menuLabel={actions.menuLabel}
                />
              </Box>
            </TourTarget>
          )}
        </Box>

        <Box flex align='center' wrap gap='x4' mt='x8'>
          <Box
            flex
            align='center'
            justify='center'
            gap='x4'
            pl='x8'
            pr='x4'
            radius='round'
            bg='surface-black'
          >
            <Body2 medium color='text-white'>
              {wallet.currencyCode}
            </Body2>
            <WalletCurrencyIcon currencyCode={wallet.currencyCode} size='badge' />
          </Box>

          {allActionsDisabled && (
            <Tooltip content={actions.walletUnavailableMessage} direction='bottom'>
              <ActionIcon
                as='button'
                aria-label={getContentLabel(actions.walletUnavailableMessage, wallet.balanceLabel)}
                icon={{ icon: WarningIcon, color: 'text-high-em' }}
                size='x24'
                shape='circle'
                bg='surface-warning-high-em'
              />
            </Tooltip>
          )}

          <Body2 medium color='text-med-em' sensitive>
            {wallet.walletNumber}
          </Body2>
          <CopyLink
            value={wallet.walletNumber}
            tooltipText={isMobile ? undefined : wallet.copyWalletNumberLabel}
          >
            <ActionIcon
              as='button'
              icon={{ icon: CopyIcon, color: 'text-med-em' }}
              size='x16'
              aria-label={wallet.copyWalletNumberLabel}
            />
          </CopyLink>
        </Box>

        <Loading
          isLoading={!!wallet.isBreakdownLoading}
          showLoadingIcon
          fullWidth
          wrapperClass={styles.breakdownLoading}
        >
          {wallet.breakdownErrorMessage ? (
            <Body1 color='text-danger-high-em'>{wallet.breakdownErrorMessage}</Body1>
          ) : (
            <Box flex direction='column' gap='x8' mt='x24'>
              {wallet.breakdown.map((item) => (
                <Box key={item.id} flex align='center' justify='space-between' gap='x24'>
                  <Body2 color='text-med-em'>{item.label}</Body2>
                  {item.onSelect ? (
                    <SensitiveData>
                      <Link onClick={item.onSelect}>{item.value}</Link>
                    </SensitiveData>
                  ) : (
                    <Body2 color='text-high-em' sensitive>
                      {item.value}
                    </Body2>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Loading>

        {mobileActions.length > 0 && (
          <Box className={styles.mobileActions}>
            {mobileActions.map((action) => {
              const isPrimary = action.kind === actions.primaryAction
              const isInteractionDisabled = !!action.disabled || !!action.loading
              const ButtonComponent = isPrimary ? IconButton.Dark : IconButton.Secondary

              return (
                <Tooltip
                  key={action.kind}
                  content={actions.actionUnavailableMessage}
                  disabled={!action.disabled || !!action.loading}
                  wrapperClassName={styles.mobileActionTooltip}
                >
                  <TourTarget target={actionTourTargets[action.kind]}>
                    <Box flex direction='column' align='center' fullWidth gap='x12'>
                      <ButtonComponent
                        icon={actionIcons[action.kind]}
                        size='xl'
                        disabled={isInteractionDisabled}
                        onClick={action.onClick}
                        aria-label={action.label}
                        aria-busy={action.loading || undefined}
                      />
                      <Body2 color='text-high-em'>{action.label}</Body2>
                    </Box>
                  </TourTarget>
                </Tooltip>
              )
            })}
          </Box>
        )}
      </section>
    </TourTarget>
  )
}
